import { db } from '../firebase';

/**
 * Firebase Spark Plan (Free Tier) Limits:
 * - Firestore Reads: 50,000 / day (1,500,000 / month)
 * - Firestore Writes: 20,000 / day (600,000 / month)
 * - Firestore Deletes: 20,000 / day
 * - Firestore Stored Data: 1 GB (1,024 MB)
 * - Cloud Storage Data: 5 GB (5,120 MB)
 * - Cloud Storage Downloads: 1 GB / day (1,024 MB / day)
 * - Cloud Storage Uploads: 20,000 / day
 * - Cloud Messaging (FCM): Unlimited & 100% Free
 * - Authentication: Phone 10/day, Email/Google Unlimited
 */
export const SPARK_LIMITS = {
  firestoreDailyReads: 50000,
  firestoreMonthlyReads: 1500000,
  firestoreDailyWrites: 20000,
  firestoreMonthlyWrites: 600000,
  firestoreDailyDeletes: 20000,
  firestoreStorageMB: 1024,
  cloudStorageMB: 5120,
  cloudStorageDailyDownloadMB: 1024,
  cloudStorageDailyUploads: 20000,
  fcm: 'Unlimited (100% Free)',
};

/**
 * Aggregates collection counts and estimates Firestore & Storage daily/monthly consumption.
 */
export async function getFirebaseUsageData() {
  try {
    // 1. Gather document counts across key collections
    const collectionsToCount = [
      'users',
      'hadiths',
      'duas',
      'quotes',
      'surahs',
      'banners',
      'prophets',
      'adhkar',
      'notifications',
      'in_app_campaigns',
      'support_tickets',
      'feedback',
      'messages',
      'gallery',
    ];

    const countPromises = collectionsToCount.map(async (col) => {
      try {
        const snap = await db.collection(col).get();
        return { name: col, count: snap.size };
      } catch (e) {
        return { name: col, count: 0 };
      }
    });

    const collectionCounts = await Promise.all(countPromises);
    const totalDocs = collectionCounts.reduce((acc, curr) => acc + curr.count, 0);

    // 2. Count today's active users from 'users'
    let activeTodayCount = 0;
    let newUsersTodayCount = 0;
    try {
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const activeSnap = await db.collection('users').where('lastActive', '>=', todayStart).get();
      activeTodayCount = activeSnap.size;
      const newSnap = await db.collection('users').where('createdAt', '>=', todayStart).get();
      newUsersTodayCount = newSnap.size;
    } catch (_) {}

    // 3. Estimated Daily Firestore Operations:
    // - Each active user performs on avg ~12 reads per session (app startup, prayers, quotes, config)
    // - Admin web queries: ~200-500 reads
    const estimatedDailyReads = Math.max(150, Math.round(activeTodayCount * 14 + 350));
    const estimatedMonthlyReads = Math.round(estimatedDailyReads * 30);

    // - Each active user performs on avg ~2 writes (lastActive, history/preferences)
    // - Plus new users registration writes (~3 writes per user)
    const estimatedDailyWrites = Math.max(50, Math.round(activeTodayCount * 2 + newUsersTodayCount * 3 + 40));
    const estimatedMonthlyWrites = Math.round(estimatedDailyWrites * 30);

    // 4. Stored Firestore Data Estimation:
    // Avg Firestore doc with indexes is ~1.5 KB
    const estimatedFirestoreDataMB = Number(((totalDocs * 1.5) / 1024).toFixed(2));

    // 5. Cloud Storage Estimation:
    // Media files from banners, prophets, gallery, quotes
    const galleryCount = collectionCounts.find((c) => c.name === 'gallery')?.count || 0;
    const bannerCount = collectionCounts.find((c) => c.name === 'banners')?.count || 0;
    const prophetCount = collectionCounts.find((c) => c.name === 'prophets')?.count || 0;
    const estimatedImageCount = galleryCount + bannerCount + prophetCount + 10;
    // Avg compressed mobile image: ~180 KB
    const estimatedCloudStorageMB = Number(((estimatedImageCount * 180) / 1024).toFixed(2));
    const estimatedDailyDownloadMB = Number(((activeTodayCount * 1.5 * 180) / 1024).toFixed(2));

    // 6. Remaining Quota Calculations
    const readsRemainingToday = Math.max(0, SPARK_LIMITS.firestoreDailyReads - estimatedDailyReads);
    const writesRemainingToday = Math.max(0, SPARK_LIMITS.firestoreDailyWrites - estimatedDailyWrites);
    const readsRemainingMonthly = Math.max(0, SPARK_LIMITS.firestoreMonthlyReads - estimatedMonthlyReads);
    const writesRemainingMonthly = Math.max(0, SPARK_LIMITS.firestoreMonthlyWrites - estimatedMonthlyWrites);
    const firestoreStorageRemainingMB = Math.max(0, Number((SPARK_LIMITS.firestoreStorageMB - estimatedFirestoreDataMB).toFixed(2)));
    const cloudStorageRemainingMB = Math.max(0, Number((SPARK_LIMITS.cloudStorageMB - estimatedCloudStorageMB).toFixed(2)));

    // 7. Calculate Percentage Used
    const readsPercentToday = Number(((estimatedDailyReads / SPARK_LIMITS.firestoreDailyReads) * 100).toFixed(1));
    const writesPercentToday = Number(((estimatedDailyWrites / SPARK_LIMITS.firestoreDailyWrites) * 100).toFixed(1));
    const firestoreStoragePercent = Number(((estimatedFirestoreDataMB / SPARK_LIMITS.firestoreStorageMB) * 100).toFixed(1));
    const cloudStoragePercent = Number(((estimatedCloudStorageMB / SPARK_LIMITS.cloudStorageMB) * 100).toFixed(1));

    // Calculate time until next midnight PST (Firebase quota daily reset time)
    const now = new Date();
    const nowUtc = now.getTime() + now.getTimezoneOffset() * 60000;
    // Midnight PST is 08:00 UTC (or 07:00 UTC during PDT)
    const nextReset = new Date(nowUtc);
    nextReset.setUTCHours(8, 0, 0, 0);
    if (nextReset.getTime() <= nowUtc) {
      nextReset.setUTCDate(nextReset.getUTCDate() + 1);
    }
    const msUntilReset = nextReset.getTime() - nowUtc;
    const hoursUntilReset = Math.floor(msUntilReset / (1000 * 60 * 60));
    const minsUntilReset = Math.floor((msUntilReset % (1000 * 60 * 60)) / (1000 * 60));

    return {
      limits: SPARK_LIMITS,
      totalDocs,
      collectionCounts,
      activeTodayCount,
      newUsersTodayCount,
      estimatedDailyReads,
      estimatedMonthlyReads,
      readsRemainingToday,
      readsRemainingMonthly,
      readsPercentToday,
      estimatedDailyWrites,
      estimatedMonthlyWrites,
      writesRemainingToday,
      writesRemainingMonthly,
      writesPercentToday,
      estimatedFirestoreDataMB,
      firestoreStorageRemainingMB,
      firestoreStoragePercent,
      estimatedCloudStorageMB,
      cloudStorageRemainingMB,
      cloudStoragePercent,
      estimatedDailyDownloadMB,
      resetTimer: `${hoursUntilReset}h ${minsUntilReset}m until daily quota resets`,
      plan: 'Spark (Free Tier)',
      status: readsPercentToday > 85 || writesPercentToday > 85 ? 'warning' : 'healthy',
    };
  } catch (error) {
    console.error('Failed to get Firebase usage data:', error);
    return null;
  }
}
